import React, { useMemo, useEffect, useState } from "react";
import {
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableToolbar,
  TableToolbarSearch,
  Pagination,
  DataTableSkeleton,
  ToastNotification,
  TableBatchActions,
  TableBatchAction,
  TableSelectAll,
  TableSelectRow,
} from "@carbon/react";
import { CheckmarkOutline, MisuseOutline } from "@carbon/icons-react";
import { useOutletContext } from "react-router-dom";
import { useFuelRequestsStore } from "../../../store/fuelRequestStore";
import { fuelRequestsMapper } from "../../../mappers/fuelRequestsMapper";
import { useTranslation } from "react-i18next";
import RequestModal from "../../../components/requestModal/RequestModal";
import { Slide, ToastContainer, toast } from "react-toastify";
import RequestProcessingModal from "../../../components/requestProcessingModal/RequestProcessingModal";

export default function FuelRequests() {
  const { isRequestsPending } = useOutletContext();
  const { t } = useTranslation();

  const [errorQueue, setErrorQueue] = useState([]);
  const [modalItem, setModalItem] = useState({
    open: false,
    type: null,
    ids: [],
    totalFuel: 0,
    client: "",
  });
  const [modalSingleItem, setModalSingleItem] = useState({
    open: false,
    item: {},
  });

  const {
    fuelRequestsData,
    fuelRequestsFilters,
    setFuelRequestsFilters,
    resetFuelRequestsCount,
    selectedFuelRequestIds,
    setSelectedFuelRequestIds,
    clearSelectedFuelRequestIds,
  } = useFuelRequestsStore();

  const { search, pagination, sort } = fuelRequestsFilters;

  const { mappedHeaders, mappedRows } = fuelRequestsMapper(
    fuelRequestsData,
    t,
    setModalSingleItem
  );

  const filteredRows = useMemo(() => {
    if (!search.trim()) return mappedRows;

    return mappedRows.filter((row) =>
      Object.values(row).some((value) => {
        if (React.isValidElement(value)) {
          value = value.props?.children || "";
        }

        return String(value).toLowerCase().includes(search.toLowerCase());
      })
    );
  }, [mappedRows, search]);

  const sortedRows = useMemo(() => {
    if (!sort.key || sort.direction === "NONE") return filteredRows;

    const extractText = (val) => {
      if (val == null) return "";

      if (typeof val === "string" || typeof val === "number") {
        return val.toString();
      }

      if (React.isValidElement(val)) {
        return extractText(val.props?.children);
      }

      if (Array.isArray(val)) {
        return val.map(extractText).join(" ");
      }

      return "";
    };

    const parseValue = (val) => {
      const raw = extractText(val).trim();

      // Проверка: число
      const number = Number(raw);
      if (!isNaN(number) && raw !== "") {
        return { type: "number", value: number };
      }

      // Проверка: дата
      const date = Date.parse(raw);
      if (!isNaN(date)) {
        return { type: "date", value: date };
      }

      return { type: "string", value: raw.toLowerCase() };
    };

    const compare = (aRaw, bRaw) => {
      const a = parseValue(aRaw);
      const b = parseValue(bRaw);

      if (a.type === b.type) {
        if (a.value === b.value) return 0;
        return a.value > b.value ? 1 : -1;
      }

      const priority = { number: 1, date: 2, string: 3 };
      return priority[a.type] - priority[b.type];
    };

    const sorted = [...filteredRows].sort((a, b) => {
      const aVal = a[sort.key];
      const bVal = b[sort.key];

      const result = compare(aVal, bVal);
      return sort.direction === "ASC" ? result : -result;
    });

    return sorted;
  }, [filteredRows, sort]);

  const paginatedRows = useMemo(() => {
    const start = (pagination.page - 1) * pagination.pageSize;
    const end = start + pagination.pageSize;
    return sortedRows.slice(start, end);
  }, [sortedRows, pagination]);

  useEffect(() => {
    resetFuelRequestsCount();
  }, [resetFuelRequestsCount]);

  useEffect(() => {
    return () => {
      toast.clearWaitingQueue({ containerId: "fuel-requests" });
      toast.dismiss({ containerId: "fuel-requests" });
    };
  }, []);

  const mapCodeToNotification = (code, requestCode) => {
    switch (code) {
      case "NO_BALANCE":
        return {
          title: t("main.fuel.fuelRequestNotification.titleNoBalance"),
          subtitle: requestCode
            ? `${t("main.fuel.fuelRequestsTable.code")} - ${requestCode} ${t(
                "main.fuel.fuelRequestNotification.subtitleNoBalance"
              )}`
            : t("main.fuel.fuelRequestNotification.subtitleNoBalance"),
        };

      case "CANNOT_APPROVE":
        return {
          title: t("main.fuel.fuelRequestNotification.titleElse"),
          subtitle: requestCode
            ? `${t("main.fuel.fuelRequestsTable.code")} - ${requestCode} ${t(
                "main.fuel.fuelRequestNotification.subtitleElse"
              )}`
            : t("main.fuel.fuelRequestNotification.subtitleElse"),
        };

      default:
        return {
          title: t("main.fuel.fuelRequestNotification.titleElse"),
          subtitle: t("main.fuel.fuelRequestNotification.subtitleElse"),
        };
    }
  };

  useEffect(() => {
    if (!errorQueue.length) return;

    errorQueue.forEach(({ kind, code, requestCode }, index) => {
      const notification = mapCodeToNotification(code, requestCode);

      toast[kind](
        ({ closeToast }) => (
          <ToastNotification
            kind={kind}
            title={notification.title}
            subtitle={notification.subtitle}
            onCloseButtonClick={closeToast}
            onClose={() => false}
            hidden={false}
            hideCloseButton={false}
            className="toast_notification"
            statusIconDescription=""
            aria-label={t("main.modalText.close")}
          />
        ),
        {
          containerId: "fuel-requests",
          toastId: `${kind}-${code}-${index}-${Date.now()}`,
          position: "bottom-right",
        }
      );
    });

    setErrorQueue([]);
  }, [errorQueue, t]);

  const getTotalItemsFuel = (selectedRows) => {
    return selectedRows.reduce((sum, row) => {
      const fuelCell = row.cells.find((cell) => cell.id.endsWith(":fuel"));
      return sum + (fuelCell ? Number(fuelCell.value) : 0);
    }, 0);
  };

  const getItemsClient = (selectedRows, limit = 3) => {
    const clients = [
      ...new Set(
        selectedRows
          .map((row) => row.cells.find((c) => c.id.endsWith(":client"))?.value)
          .filter(Boolean)
      ),
    ];

    return clients.length > limit
      ? `${clients.slice(0, limit).join(", ")}…`
      : clients.join(", ");
  };

  const getRequestCodesMap = (selectedRows) => {
    return selectedRows.reduce((acc, row) => {
      const code = row.cells.find((c) => c.id.endsWith(":code"))?.value;
      if (code) {
        acc[row.id] = code;
      }
      return acc;
    }, {});
  };

  const openModal = (type, selectedRows) => {
    setModalItem({
      open: true,
      type,
      ids: selectedRows.map((r) => Number(r.id)),
      totalFuel: getTotalItemsFuel(selectedRows),
      client: getItemsClient(selectedRows),
      codesMap: getRequestCodesMap(selectedRows),
    });
  };

  const handleSelectAll = (rows, event) => {
    const checked = event.target.checked;

    if (checked) {
      setSelectedFuelRequestIds(rows.map((row) => Number(row.id)));
    } else {
      clearSelectedFuelRequestIds();
    }
  };

  const handleRowSelect = (row, event) => {
    const checked = event.target.checked;
    const id = Number(row.id);

    if (checked) {
      if (!selectedFuelRequestIds.includes(id)) {
        setSelectedFuelRequestIds([...selectedFuelRequestIds, id]);
      }
    } else {
      setSelectedFuelRequestIds(
        selectedFuelRequestIds.filter((item) => item !== id)
      );
    }
  };

  return (
    <div className="main">
      {isRequestsPending ? (
        <DataTableSkeleton
          aria-label="sample table"
          headers={[
            {
              header: `${t("main.fuel.fuelRequestsTable.code")}`,
              key: "code",
            },
            {
              header: `${t("main.fuel.fuelRequestsTable.client")}`,
              key: "client",
            },
            {
              header: `${t("main.fuel.fuelRequestsTable.created")}`,
              key: "created",
            },
            {
              header: `${t("main.fuel.fuelRequestsTable.fuel")}`,
              key: "fuel",
            },
            {
              header: `${t("main.fuel.fuelRequestsTable.status")}`,
              key: "status",
            },
          ]}
          showHeader
          showToolbar
        />
      ) : (
        <DataTable
          size="lg"
          headers={mappedHeaders}
          rows={paginatedRows}
          isSortable
          render={({
            rows,
            headers,
            getHeaderProps,
            getSelectionProps,
            getBatchActionProps,
            onInputChange,
            selectedRows,
          }) => {
            const allSelected =
              rows.length > 0 &&
              rows.every((row) =>
                selectedFuelRequestIds.includes(Number(row.id))
              );

            const someSelected = rows.some((row) =>
              selectedFuelRequestIds.includes(Number(row.id))
            );
            const selectableRows = rows.filter((row) => !row.disabled);
            const isSelectAllDisabled = selectableRows.length === 0;
            const batchProps = getBatchActionProps();
            const controlledBatchProps = {
              ...batchProps,
              totalSelected: selectedFuelRequestIds.length,
              shouldShowBatchActions: selectedFuelRequestIds.length > 0,
            };
            const controlledSelectedRows = rows.filter((row) =>
              selectedFuelRequestIds.includes(Number(row.id))
            );
            return (
              <TableContainer
                title={`${t("main.fuel.fuelRequestsTable.title")}`}
                description={`${
                  mappedRows.length
                    ? ""
                    : `${t("main.fuel.fuelRequestsTable.empty")}`
                }`}
              >
                <TableToolbar>
                  <TableBatchActions
                    {...controlledBatchProps}
                    onCancel={() => clearSelectedFuelRequestIds()}
                    translateWithId={(id) => {
                      const total = selectedFuelRequestIds.length;
                      const translations = {
                        "carbon.table.batch.cancel": `${t(
                          "main.fuel.fuelRequestsActions.cancel"
                        )}`,
                        "carbon.table.batch.items.selected":
                          total > 4
                            ? `${total} ${t(
                                "main.fuel.fuelRequestsActions.itemsSelected"
                              )} - ${getTotalItemsFuel(
                                controlledSelectedRows
                              )} NZT`
                            : `${total} ${t(
                                "main.fuel.fuelRequestsActions.items2-4Selected"
                              )} - ${getTotalItemsFuel(
                                controlledSelectedRows
                              )} NZT`,
                        "carbon.table.batch.item.selected": `${total} ${t(
                          "main.fuel.fuelRequestsActions.itemSelected"
                        )} - ${getTotalItemsFuel(controlledSelectedRows)} NZT`,
                      };
                      return translations[id] || id;
                    }}
                  >
                    <TableBatchAction
                      onClick={() => {
                        openModal("approve", controlledSelectedRows);
                        clearSelectedFuelRequestIds();
                        batchProps.onCancel();
                      }}
                      renderIcon={CheckmarkOutline}
                    >
                      {t("main.fuel.fuelRequestsActions.approve")}
                    </TableBatchAction>
                    <TableBatchAction
                      onClick={() => {
                        openModal("decline", controlledSelectedRows);
                        clearSelectedFuelRequestIds();
                        batchProps.onCancel();
                      }}
                      renderIcon={MisuseOutline}
                    >
                      {t("main.fuel.fuelRequestsActions.decline")}
                    </TableBatchAction>
                  </TableBatchActions>

                  <TableToolbarSearch
                    value={search}
                    defaultExpanded={!!search}
                    persistent={!!search}
                    placeholder={`${t("main.tableText.placeholder")}`}
                    closeButtonLabelText={`${t(
                      "main.searchText.closeButtonText"
                    )}`}
                    onChange={(e) => {
                      setFuelRequestsFilters({
                        ...fuelRequestsFilters,
                        search: e.target.value,
                        pagination: {
                          ...fuelRequestsFilters.pagination,
                          page: 1,
                        },
                      });
                    }}
                  />
                </TableToolbar>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableSelectAll
                        {...getSelectionProps()}
                        checked={allSelected}
                        indeterminate={!allSelected && someSelected}
                        onSelect={(event) =>
                          handleSelectAll(selectableRows, event)
                        }
                        disabled={isSelectAllDisabled}
                      />
                      {headers.map((header) => {
                        const sort = fuelRequestsFilters.sort;
                        const isSorted = sort.key === header.key;

                        const { key, ...headerProps } = getHeaderProps({
                          header,
                          onClick: () => {
                            let nextDirection;

                            if (sort.key !== header.key) {
                              nextDirection = "ASC";
                            } else if (sort.direction === "ASC") {
                              nextDirection = "DESC";
                            } else if (sort.direction === "DESC") {
                              nextDirection = "NONE";
                            } else {
                              nextDirection = "ASC";
                            }

                            setFuelRequestsFilters({
                              sort: {
                                key: header.key,
                                direction: nextDirection,
                              },
                            });
                          },
                        });

                        return (
                          <TableHeader
                            key={key}
                            {...headerProps}
                            isSortHeader={isSorted && sort.direction !== "NONE"}
                            sortDirection={isSorted ? sort.direction : "NONE"}
                          >
                            {t(header.header)}
                          </TableHeader>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.id}>
                        <TableSelectRow
                          {...getSelectionProps({ row })}
                          checked={selectedFuelRequestIds.includes(
                            Number(row.id)
                          )}
                          onSelect={(event) => handleRowSelect(row, event)}
                        />
                        {row.cells.map((cell) => (
                          <TableCell key={cell.id}>{cell.value}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {paginatedRows.length ? (
                  <Pagination
                    backwardText={`${t(
                      "main.tableText.pagination.backwardText"
                    )}`}
                    forwardText={`${t(
                      "main.tableText.pagination.forwardText"
                    )}`}
                    itemsPerPageText={`${t(
                      "main.tableText.pagination.itemsPerPageText"
                    )}`}
                    page={pagination.page}
                    onChange={({ page, pageSize }) => {
                      setFuelRequestsFilters({
                        ...fuelRequestsFilters,
                        pagination: { page, pageSize },
                      });
                    }}
                    pageNumberText={`${t(
                      "main.tableText.pagination.pageNumberText"
                    )}`}
                    itemRangeText={(min, max, total) =>
                      `${min}–${max} ${t(
                        "main.tableText.pagination.range"
                      )} ${total} ${
                        total === 1
                          ? t("main.tableText.pagination.itemRangeText.item")
                          : t("main.tableText.pagination.itemRangeText.items")
                      }`
                    }
                    pageRangeText={(current, total) =>
                      `${t("main.tableText.pagination.range")} ${total} ${
                        total === 1
                          ? t("main.tableText.pagination.pageRangeText.page")
                          : t("main.tableText.pagination.pageRangeText.pages")
                      }`
                    }
                    pageSize={pagination.pageSize}
                    pageSizes={[10, 15, 20]}
                    size="md"
                    totalItems={filteredRows.length}
                  />
                ) : null}
              </TableContainer>
            );
          }}
        />
      )}

      <ToastContainer
        containerId="fuel-requests"
        position="bottom-right"
        transition={Slide}
        icon={false}
        hideProgressBar={true}
        closeButton={false}
      />
      {modalItem.open && (
        <RequestProcessingModal
          modalItem={modalItem}
          setModalItem={setModalItem}
          setErrorQueue={setErrorQueue}
        />
      )}
      {modalSingleItem.open && (
        <RequestModal
          modalItem={modalSingleItem}
          setModalItem={setModalSingleItem}
          setErrorQueue={setErrorQueue}
        />
      )}
    </div>
  );
}
